import { RefObject, useLayoutEffect, useRef } from 'react';

export interface HotspotZoomAnchor {
  x: number;
  y: number;
  clientX: number;
  clientY: number;
}

/** Image-only input. Ordinary scrolling and hotspot activation stay native. */
export function useHotspotGestures(
  enabled: boolean,
  viewportRef: RefObject<HTMLDivElement>,
  imageRef: RefObject<HTMLDivElement>,
  zoom: number,
  maxZoom: number,
  usefulZoom: number,
  changeZoom: (next: number, anchor?: HotspotZoomAnchor) => void
) {
  const latest = useRef({ zoom, maxZoom, usefulZoom, changeZoom });
  latest.current = { zoom, maxZoom, usefulZoom, changeZoom };

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!enabled || !viewport) return;
    type Point = { clientX: number; clientY: number };
    type Tap = Point & { time: number };
    let tap: Tap | null = null;
    let previousTap: Tap | null = null;
    let pinch: {
      distance: number;
      zoom: number;
      anchor: HotspotZoomAnchor;
    } | null = null;
    const distance = (a: Point, b: Point) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const interactive = (target: EventTarget | null) =>
      target instanceof Element &&
      !!target.closest(
        'button, a, input, select, textarea, [role="button"], [role="dialog"], [contenteditable="true"]'
      );
    const anchorAt = (point: Point): HotspotZoomAnchor | undefined => {
      const image = imageRef.current?.getBoundingClientRect();
      if (!image?.width || !image.height) return;
      return {
        clientX: point.clientX,
        clientY: point.clientY,
        x: (point.clientX - image.left) / image.width,
        y: (point.clientY - image.top) / image.height
      };
    };
    const apply = (next: number, anchor?: HotspotZoomAnchor) => {
      const state = latest.current;
      const level = Math.max(1, Math.min(state.maxZoom, next));
      if (!Number.isFinite(level)) return;
      // More than one input event can arrive before the next React render.
      state.zoom = level;
      state.changeZoom(level, anchor);
    };
    const toggle = (point: Point) =>
      apply(
        latest.current.zoom > 1 ? 1 : Math.max(2, latest.current.usefulZoom),
        anchorAt(point)
      );
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey || !event.cancelable || interactive(event.target))
        return;
      event.preventDefault();
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? viewport.clientHeight
            : 1;
      apply(
        latest.current.zoom * Math.exp((-event.deltaY * unit) / 300),
        anchorAt(event)
      );
    };
    const doubleClick = (event: MouseEvent) => {
      if (interactive(event.target)) return;
      event.preventDefault();
      toggle(event);
    };
    const keyDown = (event: KeyboardEvent) => {
      if (
        event.target !== viewport ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      )
        return;
      const factor =
        event.key === '+' || event.key === '='
          ? 1.5
          : event.key === '-'
            ? 1 / 1.5
            : null;
      if (factor === null && event.key !== '0') return;
      event.preventDefault();
      apply(event.key === '0' ? 1 : latest.current.zoom * factor!);
    };
    const midpoint = (touches: TouchList): Point => ({
      clientX: (touches[0].clientX + touches[1].clientX) / 2,
      clientY: (touches[0].clientY + touches[1].clientY) / 2
    });
    const touchStart = (event: TouchEvent) => {
      if (interactive(event.target)) return;
      if (event.touches.length === 2 && event.cancelable) {
        const anchor = anchorAt(midpoint(event.touches));
        const span = distance(event.touches[0], event.touches[1]);
        if (anchor && span > 0) {
          event.preventDefault();
          pinch = { anchor, distance: span, zoom: latest.current.zoom };
        }
        tap = previousTap = null;
      } else if (event.touches.length === 1) {
        tap = {
          clientX: event.touches[0].clientX,
          clientY: event.touches[0].clientY,
          time: event.timeStamp
        };
      } else {
        tap = previousTap = pinch = null;
      }
    };
    const touchMove = (event: TouchEvent) => {
      if (pinch && event.touches.length === 2 && event.cancelable) {
        event.preventDefault();
        apply(
          (pinch.zoom * distance(event.touches[0], event.touches[1])) /
            pinch.distance,
          {
            ...pinch.anchor,
            ...midpoint(event.touches)
          }
        );
      } else if (
        tap &&
        event.touches.length === 1 &&
        distance(tap, event.touches[0]) > 10
      ) {
        tap = previousTap = null;
      }
    };
    const touchEnd = (event: TouchEvent) => {
      if (pinch) {
        if (event.cancelable) event.preventDefault();
        pinch = null;
        tap = previousTap = null;
        return;
      }
      if (event.touches.length || !tap) return;
      const ended = event.changedTouches[0];
      if (
        ended &&
        event.timeStamp - tap.time <= 300 &&
        distance(tap, ended) <= 10
      ) {
        if (
          previousTap &&
          event.timeStamp - previousTap.time <= 300 &&
          distance(previousTap, ended) <= 24
        ) {
          if (event.cancelable) event.preventDefault();
          toggle(ended);
          previousTap = null;
        } else {
          previousTap = {
            clientX: ended.clientX,
            clientY: ended.clientY,
            time: event.timeStamp
          };
        }
      } else previousTap = null;
      tap = null;
    };
    const cancel = () => {
      tap = previousTap = pinch = null;
    };
    viewport.addEventListener('wheel', wheel, { passive: false });
    viewport.addEventListener('dblclick', doubleClick);
    viewport.addEventListener('keydown', keyDown);
    viewport.addEventListener('touchstart', touchStart, { passive: false });
    viewport.addEventListener('touchmove', touchMove, { passive: false });
    viewport.addEventListener('touchend', touchEnd, { passive: false });
    viewport.addEventListener('touchcancel', cancel);
    return () => {
      viewport.removeEventListener('wheel', wheel);
      viewport.removeEventListener('dblclick', doubleClick);
      viewport.removeEventListener('keydown', keyDown);
      viewport.removeEventListener('touchstart', touchStart);
      viewport.removeEventListener('touchmove', touchMove);
      viewport.removeEventListener('touchend', touchEnd);
      viewport.removeEventListener('touchcancel', cancel);
    };
  }, [enabled, viewportRef, imageRef]);
}
